import React, { useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "../../auth";
import { getAllStudents as loadStudents, refreshStudentsFromFirestore } from "../../services/studentsService";
import { db } from "../../services/firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { getPayments as fetchPayments, updatePayment as updatePaymentService } from "../../services/transactionsService";

export default function SISFinance() {
  const currentUser = getCurrentUser();
  const role = String(currentUser?.role || "").toLowerCase();
  const targetId = currentUser?.studentId || "";
  const isSelf = role === "student" || role === "parent";
  const [students, setStudents] = useState(() => loadStudents());
  useEffect(() => {
    (async () => {
      try {
        const list = await refreshStudentsFromFirestore();
        if (Array.isArray(list) && list.length) setStudents(list);
      } catch (e) { /* ignore */ }
    })();
    const unsub = onSnapshot(collection(db, "students"), (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      try { localStorage.setItem("students_fs_cache", JSON.stringify(list)); } catch (e) { /* ignore */ }
      setStudents(list);
    }, () => { /* ignore */ });
    return () => unsub();
  }, []);
  const userCampus = String(currentUser?.campus || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superadmin" || userCampus === "all";
  const campusStudents = useMemo(() => {
    return isAdmin ? students : students.filter(s => String(s.campus || "").toLowerCase() === userCampus);
  }, [students, isAdmin, userCampus]);
  const [selectedId, setSelectedId] = useState(() => (isSelf ? targetId : (campusStudents[0]?.id || "")));
  const [query, setQuery] = useState("");

  const [payments, setPayments] = useState([]);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "payments"), (snap) => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPayments(rows);
    }, () => {
      (async () => {
        try {
          const rows = await fetchPayments();
          setPayments(Array.isArray(rows) ? rows : []);
        } catch { setPayments([]); }
      })();
    });
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return campusStudents.slice(0, 20);
    return campusStudents
      .filter(s => {
        const name = (s.fullname || `${s.firstName || ""} ${s.lastName || ""}`).toLowerCase();
        return name.includes(q) || String(s.id).toLowerCase().includes(q);
      })
      .slice(0, 20);
  }, [query, campusStudents]);

  const student = useMemo(() => campusStudents.find(s => s.id === (isSelf ? targetId : selectedId)) || null, [isSelf, targetId, selectedId, campusStudents]);
  const studentPayments = useMemo(
    () => payments.filter(p => String(p.studentId || p.student_id || "") === String(student?.id || "")),
    [payments, student]
  );

  async function linkPaymentByReference(ref) {
    if (!student) return;
    const target = payments.find(p => String(p.reference || "").trim() === String(ref || "").trim());
    if (!target) {
      alert("No payment found with that OR number.");
      return;
    }
    try {
      const rows = await updatePaymentService(target.id, { studentId: student.id });
      setPayments(Array.isArray(rows) ? rows : []);
      alert("Payment linked to student.");
    } catch (e) {
      alert("Failed to link payment.");
    }
  }

  async function linkNameAndCampusPayments() {
    if (!student) return;
    const full = (student.fullname || `${student.firstName || ""} ${student.lastName || ""}`).trim().toLowerCase();
    const toFix = payments.filter(p => {
      const name = String(p.payer || "").trim().toLowerCase();
      const campusMatch = String(p.campus || "").toLowerCase() === String(student.campus || "").toLowerCase();
      const missingOrWrong = String(p.studentId || p.student_id || "") !== String(student.id);
      return campusMatch && missingOrWrong && (full && name.includes(full));
    });
    if (toFix.length === 0) {
      alert("No matching payments to link.");
      return;
    }
    try {
      let rows = payments;
      for (const p of toFix) {
        // eslint-disable-next-line no-await-in-loop
        rows = await updatePaymentService(p.id, { studentId: student.id });
      }
      setPayments(Array.isArray(rows) ? rows : []);
      alert(`Linked ${toFix.length} payment(s) to student.`);
    } catch (e) {
      alert("Failed to link some payments.");
    }
  }
  const totalPaid = useMemo(() => studentPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0), [studentPayments]);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Student Finance</h3>
      </div>

      {!isSelf && (
        <div className="card p-3 mb-3">
          <label className="form-label">Student</label>
          <div style={{ position: "relative", maxWidth: 720 }}>
            <input
              className="form-control"
              placeholder="Type name or ID (e.g., Warlito Castro or S-GAPAN-0001)"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && filtered.length > 0) {
                  setSelectedId(filtered[0].id);
                }
              }}
            />
            {filtered.length > 0 && (
              <div className="list-group" style={{ position: "absolute", zIndex: 10, width: "100%", maxHeight: 280, overflowY: "auto" }}>
                {filtered.map(s => {
                  const label = `${(s.fullname || `${s.firstName || ""} ${s.lastName || ""}`).trim()} (${s.id}) — ${String(s.campus).toUpperCase()}`;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`list-group-item list-group-item-action${s.id === selectedId ? " active" : ""}`}
                      onClick={() => { setSelectedId(s.id); setQuery(label); }}
                    >
                      {label}
                    </button>
                  );
                })}
                {filtered.length === 0 && <div className="list-group-item text-muted">No matches</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {student ? (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="card p-3">
                <div className="text-muted">Student</div>
                <div className="fw-bold">{student.fullname || `${student.firstName || ""} ${student.lastName || ""}`}</div>
                <div className="small text-capitalize">{student.campus}</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card p-3">
                <div className="text-muted">Grade/Strand</div>
                <div className="fw-bold">{student.gradeLevel || ""}</div>
                <div className="small">{student.strand || ""}</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card p-3">
                <div className="text-muted">Total Paid</div>
                <div className="display-6 fw-bold">₱{Number(totalPaid).toLocaleString()}</div>
              </div>
            </div>
          </div>

          <div className="card p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Payments History</h5>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {studentPayments.map((p, i) => (
                    <tr key={`${p.reference || i}-${p.date}`}>
                      <td>{p.date}</td>
                      <td>{p.method || ""}</td>
                      <td>{p.reference || ""}</td>
                      <td className="text-end">₱{Number(p.amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {studentPayments.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center text-muted">No payments found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3">
              <div className="row g-2">
                <div className="col-md-4">
                  <input className="form-control form-control-sm" placeholder="OR number" onKeyDown={e => {
                    if (e.key === "Enter") linkPaymentByReference(e.currentTarget.value);
                  }} />
                </div>
                <div className="col-md-4">
                  <button className="btn btn-outline-primary btn-sm" onClick={() => {
                    const input = document.activeElement;
                    if (input && input.placeholder === "OR number") {
                      // Use current value if focused
                      // @ts-ignore
                      const v = input.value || "";
                      linkPaymentByReference(v);
                    } else {
                      const v = window.prompt("Enter OR number to link to this student") || "";
                      if (v) linkPaymentByReference(v);
                    }
                  }}>Link OR to this student</button>
                </div>
                <div className="col-md-4">
                  <button className="btn btn-outline-secondary btn-sm" onClick={linkNameAndCampusPayments}>
                    Link name+campus matching payments
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="alert alert-warning">Student not found.</div>
      )}
    </div>
  );
}
